import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { formatEuro, formatPercent } from "@/lib/format";
import { toast } from "sonner";
import SmartLoader from "@/components/common/SmartLoader";
import {
  KeyRound, Users, ShieldCheck, FileSignature, Coins, AlertTriangle,
  PlusCircle, Download, Receipt, TrendingUp, HomeIcon, CalendarClock, RotateCcw,
  Send, Copy as CopyIcon, PowerOff, RefreshCw, Sparkles, Mail, BellRing,
} from "lucide-react";

const STATUS_TONE = {
  paid: "success", due: "neutral", partial: "warning",
  overdue: "danger", canceled: "neutral",
};
const STATUS_LABEL = {
  paid: "Payé", due: "À payer", partial: "Partiel",
  overdue: "Impayé", canceled: "Annulé",
};
const LEASE_STATUS_TONE = {
  active: "success", draft: "neutral", upcoming: "info", notice: "warning",
  renewed: "success", terminated: "neutral", archived: "neutral",
};
const LEASE_STATUS_LABEL = {
  draft: "Brouillon", upcoming: "À venir", active: "Actif",
  notice: "Préavis", renewed: "Renouvelé", terminated: "Résilié",
  archived: "Archivé",
};
const EFFECTIVE_STATUS_BADGE = {
  tacit_renewal: { tone: "info", label: "Reconduction tacite" },
  upcoming: { tone: "info", label: "À venir" },
  notice_in_progress: { tone: "warning", label: "Préavis en cours" },
};

export default function LocationsPage() {
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="animate-fade-up" data-testid="rentals-page">
      <PageHeader
        overline="Gestion locative"
        title="Locations, baux & quittances"
        description="Locataires, garants, baux, échéances, quittances PDF, dépôts de garantie et alertes."
        actions={
          <Button asChild className="btn-pill" data-testid="new-lease-cta">
            <Link to="/app/new-lease">
              <Sparkles className="mr-2 h-4 w-4" /> Nouvelle location
            </Link>
          </Button>
        }
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-8 flex-wrap h-auto">
          <TabsTrigger value="dashboard" data-testid="rent-tab-dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="tenants" data-testid="rent-tab-tenants">Locataires</TabsTrigger>
          <TabsTrigger value="guarantors" data-testid="rent-tab-guarantors">Garants</TabsTrigger>
          <TabsTrigger value="leases" data-testid="rent-tab-leases">Baux</TabsTrigger>
          <TabsTrigger value="installments" data-testid="rent-tab-installments">Échéances</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="rent-tab-receipts">Quittances</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="rent-tab-alerts">Alertes</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"><DashboardTab /></TabsContent>
        <TabsContent value="tenants"><TenantsTab /></TabsContent>
        <TabsContent value="guarantors"><GuarantorsTab /></TabsContent>
        <TabsContent value="leases"><LeasesTab /></TabsContent>
        <TabsContent value="installments"><InstallmentsTab /></TabsContent>
        <TabsContent value="receipts"><ReceiptsTab /></TabsContent>
        <TabsContent value="alerts"><AlertsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------ Dashboard ------------------ */
function DashboardTab() {
  const [dash, setDash] = useState(null);
  const [dashError, setDashError] = useState(null);
  const loadDash = () => {
    setDashError(null);
    api.get("/rent/dashboard")
      .then(({ data }) => setDash(data))
      .catch((e) => setDashError(e.response?.data?.detail || "Impossible de charger le tableau des locations."));
  };
  useEffect(() => { loadDash(); }, []);
  if (!dash || dashError) return <SmartLoader label="Chargement des locations…" onRetry={loadDash} error={dashError} />;
  const t = dash.totals;
  return (
    <div className="space-y-8">
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
        <KpiCard name="rent-expected" label={`Loyers attendus ${dash.year}`} icon={Coins} tone="accent" value={formatEuro(t.expected_ytd)} />
        <KpiCard name="rent-collected" label="Loyers encaissés" icon={TrendingUp} value={formatEuro(t.paid_ytd)} />
        <KpiCard name="rent-overdue" label="Impayés" icon={AlertTriangle} value={formatEuro(t.overdue_amount)} />
        <KpiCard name="rent-vacancy" label="Vacance" icon={HomeIcon} value={formatPercent(t.vacancy_pct)} />
      </section>
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <MiniStat label="Baux actifs" value={t.active_leases} />
        <MiniStat label="Colocations" value={t.colocations} />
        <MiniStat label="Lots occupés" value={`${t.lots_occupied}/${t.lots_total}`} />
        <MiniStat label="Dépôts détenus" value={formatEuro(t.deposits_held)} />
      </section>
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-elevated overflow-x-auto">
          <div className="px-6 py-4 border-b bg-secondary/40">
            <h3 className="font-heading text-lg flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Prochaines échéances</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary/20 text-xs uppercase tracking-wider text-muted-foreground">
              <tr className="text-left">
                <th className="px-4 py-2">Période</th>
                <th className="px-4 py-2">Échéance</th>
                <th className="px-4 py-2 text-right">Montant</th>
                <th className="px-4 py-2">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {dash.upcoming_installments.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Aucune échéance à venir.</td></tr>
              )}
              {dash.upcoming_installments.map((i) => (
                <tr key={i.id} className="hover:bg-secondary/30">
                  <td className="px-4 py-2 font-medium">{i.period_label}</td>
                  <td className="px-4 py-2 text-muted-foreground">{i.due_date}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatEuro(i.amount_expected_total)}</td>
                  <td className="px-4 py-2"><StatusBadge tone={STATUS_TONE[i.status]}>{STATUS_LABEL[i.status]}</StatusBadge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card-elevated overflow-x-auto">
          <div className="px-6 py-4 border-b bg-secondary/40">
            <h3 className="font-heading text-lg">Baux arrivant à échéance (90 jours)</h3>
          </div>
          <div className="p-4 space-y-2">
            {dash.ending_leases.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">Aucun bail à renouveler prochainement.</div>
            )}
            {dash.ending_leases.map((l) => (
              <div key={l.lease_id} className="flex items-center justify-between rounded-xl border border-border p-3">
                <div className="text-sm">
                  <div className="font-medium">Bail #{l.lease_id.slice(0, 8)}</div>
                  <div className="text-muted-foreground">Fin le {l.end_date}</div>
                </div>
                <StatusBadge tone={l.renewable ? "success" : "warning"}>
                  {l.renewable ? "Renouvelable" : "Non renouvelable"}
                </StatusBadge>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ------------------ Tenants ------------------ */
function TenantsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ kind: "person", first_name: "", last_name: "", company_name: "", email: "", phone: "", profession: "", employer: "", income_monthly: "", notes: "" });

  const load = () => api.get("/tenants", { params: q ? { q } : {} }).then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, [q]);   // eslint-disable-line

  const submit = async () => {
    try {
      const payload = { ...form };
      if (!payload.income_monthly) delete payload.income_monthly;
      await api.post("/tenants", payload);
      toast.success("Locataire créé");
      setOpen(false);
      setForm({ kind: "person", first_name: "", last_name: "", company_name: "", email: "", phone: "", profession: "", employer: "", income_monthly: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/tenants/${id}`);
    setDetail(data);
  };

  const quickInvite = async (t) => {
    if (!t.email) {
      toast.error("Ajoutez d'abord un e-mail sur la fiche locataire.");
      return;
    }
    try {
      const { data } = await api.post("/tenant-portal-admin/invite", { tenant_id: t.id });
      const st = data?.email_status;
      if (st === "sent") toast.success(`Invitation envoyée à ${t.email}`);
      else if (st === "logged") toast.success("Invitation générée (mode dev — voir logs backend)");
      else toast.success("Invitation générée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Input placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="tenant-search" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-pill" data-testid="tenant-add-btn"><PlusCircle className="mr-2 h-4 w-4" /> Nouveau locataire</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Nouveau locataire</DialogTitle>
              <DialogDescription>Personne physique ou morale. Rattaché à un bail dans un second temps.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="tenant-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="person">Personne physique</SelectItem>
                    <SelectItem value="company">Personne morale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.kind === "person" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Prénom</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="tenant-first" /></div>
                  <div><Label>Nom</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="tenant-last" /></div>
                </div>
              ) : (
                <div><Label>Raison sociale</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="tenant-company" /></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Profession</Label><Input value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} /></div>
                <div><Label>Employeur</Label><Input value={form.employer} onChange={(e) => setForm({ ...form, employer: e.target.value })} /></div>
                <div><Label>Revenu mensuel (€)</Label><Input type="number" value={form.income_monthly} onChange={(e) => setForm({ ...form, income_monthly: e.target.value })} /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} data-testid="tenant-submit">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left">
              <th className="px-6 py-3">Nom</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Profession</th>
              <th className="px-6 py-3">Contact</th>
              <th className="px-6 py-3 text-right">Revenu / mois</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground"><Users className="mx-auto h-8 w-8 mb-3 opacity-60" />Aucun locataire.</td></tr>}
            {items.map((t) => (
              <tr key={t.id} data-testid={`tenant-row-${t.id}`} className="hover:bg-secondary/30">
                <td className="px-6 py-3 font-medium">{t.display_name}</td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{t.kind === "person" ? "Personne physique" : "Personne morale"}</td>
                <td className="px-6 py-3">{t.profession || "—"}</td>
                <td className="px-6 py-3 text-xs">{t.email || t.phone || "—"}</td>
                <td className="px-6 py-3 text-right font-mono">{t.income_monthly ? formatEuro(t.income_monthly) : "—"}</td>
                <td className="px-6 py-3 text-right space-x-1">
                  <Button variant="ghost" size="sm"
                          title={t.email ? "Envoyer l'invitation à l'espace locataire" : "Ajoutez un e-mail pour inviter le locataire"}
                          onClick={() => quickInvite(t)}
                          data-testid={`tenant-quick-invite-${t.id}`}>
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openDetail(t.id)} data-testid={`tenant-open-${t.id}`}>Ouvrir</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{detail?.display_name}</DialogTitle>
            <DialogDescription>{detail?.profession ? `${detail.profession} · ` : ""}{detail?.email || detail?.phone || ""}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Info label="Adresse" value={[detail.address, detail.postal_code, detail.city].filter(Boolean).join(" ") || "—"} />
                <Info label="Employeur" value={detail.employer || "—"} />
                <Info label="Type contrat" value={detail.contract_type || "—"} />
                <Info label="Naissance" value={detail.birth_date || "—"} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Historique des baux</div>
                <div className="space-y-2">
                  {(detail.lease_history || []).length === 0 && <div className="text-muted-foreground text-xs">Aucun bail.</div>}
                  {(detail.lease_history || []).map((h) => (
                    <div key={h.lease_id} className="rounded-xl border p-3 flex justify-between items-center">
                      <div>
                        <div className="font-medium">{h.building_name} — {h.lot_reference}</div>
                        <div className="text-xs text-muted-foreground">{h.start_date} → {h.end_date || "en cours"} · {h.lease_type}</div>
                      </div>
                      <div className="text-right"><div className="font-mono">{formatEuro(h.rent_ttc)}</div><StatusBadge tone={LEASE_STATUS_TONE[h.status]}>{h.status}</StatusBadge></div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Espace locataire</div>
                <TenantPortalPanel tenant={detail} onChange={() => openDetail(detail.id)} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Garants</div>
                {(detail.guarantors || []).length === 0 ? (
                  <div className="text-muted-foreground text-xs">Aucun garant.</div>
                ) : (
                  <div className="space-y-2">
                    {detail.guarantors.map((g) => (
                      <div key={g.id} className="rounded-xl border p-3 text-sm">
                        <div className="font-medium">{g.company_name || `${g.first_name || ""} ${g.last_name || ""}`}</div>
                        <div className="text-xs text-muted-foreground">{g.guarantee_kind} · plafond {formatEuro(g.guarantee_cap_amount || 0)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {detail.notes && <div className="rounded-xl bg-secondary/40 p-4 text-sm">{detail.notes}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------ Tenant portal panel (in detail dialog) ------------------ */
const PORTAL_STATUS = {
  not_invited:   { label: "Non invité",         tone: "neutral", hint: "Aucune invitation envoyée pour l'instant." },
  invited:       { label: "Invitation envoyée", tone: "info",    hint: "Le locataire a reçu un lien. En attente de première ouverture." },
  opened:        { label: "Invitation ouverte", tone: "info",    hint: "Le locataire a cliqué sur le lien, mais n'a pas encore défini son mot de passe." },
  activated:     { label: "Compte activé",      tone: "success", hint: "Le locataire s'est connecté avec succès." },
  expired:       { label: "Invitation expirée", tone: "warning", hint: "Le dernier lien a expiré. Renvoyez une invitation." },
  disabled:      { label: "Accès désactivé",    tone: "danger",  hint: "L'accès a été révoqué. Réinviter pour réactiver." },
  missing_email: { label: "E-mail requis",      tone: "warning", hint: "Renseignez l'e-mail du locataire pour pouvoir l'inviter." },
};

function TenantPortalPanel({ tenant, onChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tenant-portal-admin/tenants/${tenant.id}/portal-status`);
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Statut indisponible");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (tenant?.id) loadStatus(); /* eslint-disable-next-line */ }, [tenant?.id]);

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tenant-portal-admin/invite", { tenant_id: tenant.id });
      setLastLink(data.invite_link || null);
      const emailStatus = data.email_status;
      if (emailStatus === "sent") toast.success("Invitation envoyée par e-mail");
      else if (emailStatus === "logged") toast.success("Invitation créée — e-mail en mode dev (voir logs)");
      else toast.success("Invitation créée");
      await loadStatus();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Désactiver l'accès locataire ? Vous pourrez le réactiver en le réinvitant.")) return;
    setBusy(true);
    try {
      await api.post(`/tenant-portal-admin/tenants/${tenant.id}/revoke`);
      toast.success("Accès désactivé");
      setLastLink(null);
      await loadStatus();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Révocation impossible");
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!lastLink) return;
    navigator.clipboard.writeText(lastLink).then(
      () => toast.success("Lien copié dans le presse-papier"),
      () => toast.error("Impossible de copier — sélectionnez le lien manuellement"),
    );
  };

  if (loading) return <div className="text-xs text-muted-foreground">Chargement du statut…</div>;
  if (!status) return null;
  const info = PORTAL_STATUS[status.status] || PORTAL_STATUS.not_invited;
  const canInvite = status.status !== "missing_email";
  const isActive = status.status === "activated" || status.status === "invited" || status.status === "opened";

  return (
    <div className="rounded-xl border border-border p-4 space-y-3" data-testid="tenant-portal-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <StatusBadge tone={info.tone} data-testid="tenant-portal-status">{info.label}</StatusBadge>
          {status.last_login_at && (
            <span className="text-xs text-muted-foreground">
              Dernière connexion : {new Date(status.last_login_at).toLocaleString("fr-FR")}
            </span>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={loadStatus} disabled={busy}>
          <RefreshCw className="h-3 w-3 mr-1" /> Actualiser
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{info.hint}</p>

      {!tenant.email && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-900">
          Ajoutez un e-mail sur la fiche locataire pour pouvoir l&apos;inviter.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {canInvite && (
          <Button
            size="sm"
            onClick={invite}
            disabled={busy}
            data-testid="tenant-invite-btn"
          >
            <Send className="h-3 w-3 mr-1.5" />
            {status.status === "not_invited"
              ? "Inviter dans l'espace locataire"
              : status.status === "disabled"
                ? "Réactiver et réinviter"
                : "Renvoyer l'invitation"}
          </Button>
        )}
        {isActive && (
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            onClick={revoke}
            disabled={busy}
            data-testid="tenant-revoke-btn"
          >
            <PowerOff className="h-3 w-3 mr-1.5" />Désactiver l&apos;accès
          </Button>
        )}
      </div>

      {lastLink && (
        <div className="rounded-lg bg-accent/5 border border-accent/20 p-3 text-xs">
          <div className="font-medium mb-1">Lien d&apos;invitation (7 jours)</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate font-mono text-[11px]" data-testid="tenant-invite-link">{lastLink}</code>
            <Button size="sm" variant="ghost" onClick={copyLink} type="button">
              <CopyIcon className="h-3 w-3" />
            </Button>
          </div>
          <p className="mt-1 text-muted-foreground">
            À utiliser si le locataire n&apos;a pas reçu l&apos;e-mail.
          </p>
        </div>
      )}
    </div>
  );
}


/* ------------------ Guarantors ------------------ */
function GuarantorsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "person", first_name: "", last_name: "", company_name: "", email: "", phone: "", income_monthly: "", guarantee_kind: "personnelle", guarantee_cap_amount: "" });
  const load = () => api.get("/guarantors").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try {
      const payload = { ...form };
      if (!payload.income_monthly) delete payload.income_monthly;
      if (!payload.guarantee_cap_amount) delete payload.guarantee_cap_amount;
      await api.post("/guarantors", payload);
      toast.success("Garant créé"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-pill" data-testid="guarantor-add-btn"><PlusCircle className="mr-2 h-4 w-4" /> Nouveau garant</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau garant</DialogTitle>
              <DialogDescription>Personne physique, société (Visale, banque…) ou autre.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="person">Personne physique</SelectItem><SelectItem value="company">Personne morale</SelectItem></SelectContent>
                </Select>
              </div>
              {form.kind === "person" ? (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Prénom</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                  <div><Label>Nom</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
                </div>
              ) : (<div><Label>Raison sociale</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>)}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>E-mail</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Type de garantie</Label>
                  <Select value={form.guarantee_kind} onValueChange={(v) => setForm({ ...form, guarantee_kind: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personnelle">Personnelle</SelectItem>
                      <SelectItem value="bancaire">Bancaire</SelectItem>
                      <SelectItem value="visale">Visale</SelectItem>
                      <SelectItem value="societe">Société</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Plafond (€)</Label><Input type="number" value={form.guarantee_cap_amount} onChange={(e) => setForm({ ...form, guarantee_cap_amount: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={submit} data-testid="guarantor-submit">Enregistrer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left"><th className="px-6 py-3">Nom</th><th className="px-6 py-3">Type</th><th className="px-6 py-3">Garantie</th><th className="px-6 py-3 text-right">Plafond</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={4} className="px-6 py-12 text-center text-muted-foreground"><ShieldCheck className="mx-auto h-8 w-8 mb-3 opacity-60" />Aucun garant.</td></tr>}
            {items.map((g) => (
              <tr key={g.id} className="hover:bg-secondary/30">
                <td className="px-6 py-3 font-medium">{g.company_name || `${g.first_name || ""} ${g.last_name || ""}`.trim() || "—"}</td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{g.kind === "person" ? "Personne physique" : "Personne morale"}</td>
                <td className="px-6 py-3">{g.guarantee_kind}</td>
                <td className="px-6 py-3 text-right font-mono">{g.guarantee_cap_amount ? formatEuro(g.guarantee_cap_amount) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------ Leases ------------------ */
function LeasesTab() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [lots, setLots] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [guarantors, setGuarantors] = useState([]);
  const [meta, setMeta] = useState({ lease_types: [], statuses: [], index_types: [], payment_frequencies: [] });
  const [form, setForm] = useState({
    lot_id: "", tenant_ids: [], guarantor_ids: [], lease_type: "vide",
    start_date: "", duration_months: 36, rent_hc: "", provisions_charges: "",
    payment_frequency: "monthly", payment_day: 5, deposit_amount: "",
    index_type: "IRL", index_base_quarter: "", index_base_value: "",
  });

  const load = () => {
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    api.get("/leases", { params }).then(({ data }) => setItems(data.items || []));
  };
  useEffect(() => { load(); }, [statusFilter]);   // eslint-disable-line
  useEffect(() => {
    api.get("/lots").then(({ data }) => setLots(data.items || []));
    api.get("/tenants").then(({ data }) => setTenants(data.items || []));
    api.get("/guarantors").then(({ data }) => setGuarantors(data.items || []));
    api.get("/leases/meta").then(({ data }) => setMeta(data));
  }, []);

  const openDetail = async (id) => { const { data } = await api.get(`/leases/${id}`); setDetail(data); };

  const submit = async () => {
    // Validation client — évite les allers-retours 422 et donne un message
    // clair AVANT même de solliciter le serveur.
    if (!form.lot_id) { toast.error("Sélectionnez un lot pour ce bail"); return; }
    if (!form.tenant_ids?.length) { toast.error("Ajoutez au moins un locataire"); return; }
    if (!form.start_date) { toast.error("La date de début est obligatoire"); return; }
    if (!form.rent_hc || parseFloat(form.rent_hc) <= 0) {
      toast.error("Le loyer hors charges doit être positif"); return;
    }
    if (!form.duration_months || parseInt(form.duration_months, 10) <= 0) {
      toast.error("La durée du bail doit être supérieure à 0 mois"); return;
    }
    try {
      const payload = { ...form };
      if (!payload.deposit_amount) delete payload.deposit_amount;
      if (!payload.index_base_value) delete payload.index_base_value;
      if (!payload.index_base_quarter) delete payload.index_base_quarter;
      if (!payload.provisions_charges) payload.provisions_charges = 0;
      payload.duration_months = parseInt(payload.duration_months);
      payload.payment_day = parseInt(payload.payment_day);
      payload.rent_hc = parseFloat(payload.rent_hc);
      if (payload.provisions_charges) payload.provisions_charges = parseFloat(payload.provisions_charges);
      await api.post("/leases", payload);
      toast.success("Bail créé"); setOpen(false); load();
    } catch (e) {
      // detail est désormais toujours une chaîne (normalisé par l'intercepteur)
      toast.error(e.response?.data?.detail || "Impossible de créer le bail");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-56" data-testid="lease-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {meta.statuses.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-pill" data-testid="lease-add-btn"><PlusCircle className="mr-2 h-4 w-4" /> Nouveau bail</Button></DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>Nouveau bail</DialogTitle>
              <DialogDescription>Sélectionnez le lot, ajoutez un ou plusieurs locataires et paramétrez le loyer.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
              <div><Label>Lot</Label>
                <Select value={form.lot_id} onValueChange={(v) => setForm({ ...form, lot_id: v })}>
                  <SelectTrigger data-testid="lease-lot"><SelectValue placeholder="Sélectionner un lot…" /></SelectTrigger>
                  <SelectContent>{lots.map((l) => <SelectItem key={l.id} value={l.id}>{l.reference} — {l.lot_type}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Locataires (Ctrl+clic pour plusieurs)</Label>
                <select multiple value={form.tenant_ids} onChange={(e) => setForm({ ...form, tenant_ids: Array.from(e.target.selectedOptions).map(o => o.value) })} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm min-h-24" data-testid="lease-tenants">
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.display_name}</option>)}
                </select>
              </div>
              <div><Label>Garants</Label>
                <select multiple value={form.guarantor_ids} onChange={(e) => setForm({ ...form, guarantor_ids: Array.from(e.target.selectedOptions).map(o => o.value) })} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm min-h-16">
                  {guarantors.map((g) => <option key={g.id} value={g.id}>{g.company_name || `${g.first_name || ""} ${g.last_name || ""}`}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type de bail</Label>
                  <Select value={form.lease_type} onValueChange={(v) => setForm({ ...form, lease_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.lease_types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Fréquence</Label>
                  <Select value={form.payment_frequency} onValueChange={(v) => setForm({ ...form, payment_frequency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.payment_frequencies.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Date de début</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="lease-start" /></div>
                <div><Label>Durée (mois)</Label><Input type="number" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} /></div>
                <div><Label>Loyer HC (€)</Label><Input type="number" value={form.rent_hc} onChange={(e) => setForm({ ...form, rent_hc: e.target.value })} data-testid="lease-rent" /></div>
                <div><Label>Provisions charges (€)</Label><Input type="number" value={form.provisions_charges} onChange={(e) => setForm({ ...form, provisions_charges: e.target.value })} /></div>
                <div><Label>Jour de paiement</Label><Input type="number" min="1" max="28" value={form.payment_day} onChange={(e) => setForm({ ...form, payment_day: e.target.value })} /></div>
                <div><Label>Dépôt de garantie (€)</Label><Input type="number" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} /></div>
                <div><Label>Indice</Label>
                  <Select value={form.index_type} onValueChange={(v) => setForm({ ...form, index_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.index_types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Indice base (trimestre)</Label><Input placeholder="2024-T2" value={form.index_base_quarter} onChange={(e) => setForm({ ...form, index_base_quarter: e.target.value })} /></div>
                <div><Label>Valeur indice base</Label><Input type="number" step="0.01" value={form.index_base_value} onChange={(e) => setForm({ ...form, index_base_value: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={submit} data-testid="lease-submit">Créer le bail</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left">
              <th className="px-6 py-3">Bien</th><th className="px-6 py-3">Locataires</th>
              <th className="px-6 py-3">Type</th><th className="px-6 py-3">Période</th>
              <th className="px-6 py-3 text-right">Loyer TTC</th><th className="px-6 py-3">Statut</th><th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-muted-foreground"><FileSignature className="mx-auto h-8 w-8 mb-3 opacity-60" />Aucun bail.</td></tr>}
            {items.map((l) => (
              <tr key={l.id} data-testid={`lease-row-${l.id}`} className="hover:bg-secondary/30">
                <td className="px-6 py-3"><div className="font-medium">{l.lot_reference}</div><div className="text-xs text-muted-foreground">{l.building_name}</div></td>
                <td className="px-6 py-3 text-xs">{(l.tenant_names || []).join(" · ")}</td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{l.lease_type}</td>
                <td className="px-6 py-3 text-xs">{l.start_date} → {l.end_date || "—"}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(l.rent_ttc)}</td>
                <td className="px-6 py-3">
                  <StatusBadge tone={LEASE_STATUS_TONE[l.status]}>{LEASE_STATUS_LABEL[l.status] || l.status}</StatusBadge>
                  {l.effective_status && EFFECTIVE_STATUS_BADGE[l.effective_status] && (
                    <div className="mt-1">
                      <StatusBadge tone={EFFECTIVE_STATUS_BADGE[l.effective_status].tone} className="text-[10px]">
                        {EFFECTIVE_STATUS_BADGE[l.effective_status].label}
                      </StatusBadge>
                    </div>
                  )}
                </td>
                <td className="px-6 py-3 text-right"><Button variant="ghost" size="sm" onClick={() => openDetail(l.id)} data-testid={`lease-open-${l.id}`}>Ouvrir</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LeaseDetailModal detail={detail} onClose={() => setDetail(null)} onRefresh={load} />
    </div>
  );
}

function LeaseDetailModal({ detail, onClose, onRefresh }) {
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reviseForm, setReviseForm] = useState({ new_quarter: "", new_value: "" });

  const revise = async () => {
    try {
      await api.post(`/leases/${detail.id}/irl/revise`, reviseForm);
      toast.success("Loyer révisé");
      setReviseOpen(false);
      const { data } = await api.get(`/leases/${detail.id}`);
      Object.assign(detail, data);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const terminate = async () => {
    const dateInput = window.prompt("Date de résiliation (YYYY-MM-DD) :");
    if (!dateInput) return;
    try {
      await api.post(`/leases/${detail.id}/terminate`, { termination_date: dateInput, reason: "Résiliation manuelle" });
      toast.success("Bail résilié"); onClose(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!detail) return null;
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle>{detail.lot?.reference} — {detail.lease_type}</DialogTitle>
          <DialogDescription>{detail.building?.name} · {detail.company?.name} · Bail {detail.status}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Info label="Loyer HC" value={formatEuro(detail.rent_hc)} />
            <Info label="Provisions" value={formatEuro(detail.provisions_charges)} />
            <Info label="Loyer TTC" value={formatEuro(detail.rent_ttc)} />
            <Info label="Dépôt" value={detail.deposit_amount ? formatEuro(detail.deposit_amount) : "—"} />
            <Info label="Début" value={detail.start_date} />
            <Info label="Fin" value={detail.end_date || "—"} />
            <Info label="Fréquence" value={detail.payment_frequency} />
            <Info label="Jour" value={detail.payment_day} />
          </section>
          <section>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Locataires ({detail.tenants?.length})</div>
            <div className="space-y-2">
              {detail.tenants?.map((t) => (
                <div key={t.id} className="rounded-xl border p-3 text-sm">
                  <div className="font-medium">{t.display_name}</div>
                  <div className="text-xs text-muted-foreground">{t.email || "—"} · {t.profession || "—"}</div>
                </div>
              ))}
            </div>
          </section>
          <section>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Indexation {detail.index_type}</div>
            <div className="rounded-xl border p-3 text-sm">
              <div>Base : {detail.index_base_quarter || "—"} (valeur {detail.index_base_value || "—"})</div>
              <div>Actuel : {detail.index_current_quarter || "—"} (valeur {detail.index_current_value || "—"})</div>
              {(detail.index_history || []).length > 0 && (
                <div className="mt-2 space-y-1 text-xs">
                  {(detail.index_history || []).map((h) => (
                    <div key={`idx-${h.quarter}`} className="text-muted-foreground">{h.quarter} · x{h.factor} · {formatEuro(h.previous_rent_hc)} → {formatEuro(h.new_rent_hc)} ({formatEuro(h.delta)})</div>
                  ))}
                </div>
              )}
              {detail.status === "active" && detail.index_type !== "FIXE" && (
                <Button size="sm" variant="outline" className="mt-3" onClick={() => setReviseOpen(true)} data-testid="lease-revise-btn"><RotateCcw className="mr-2 h-3 w-3" />Réviser</Button>
              )}
            </div>
          </section>
          {detail.deposit_amount && (
            <section>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Dépôt de garantie</div>
              <div className="rounded-xl border p-3 text-sm">
                <div>Montant : {formatEuro(detail.deposit_amount)} · Statut : <StatusBadge tone={detail.deposit_status === "restituted" ? "success" : "neutral"}>{detail.deposit_status}</StatusBadge></div>
                {detail.deposit_restitution_date && (
                  <div className="mt-1 text-xs text-muted-foreground">Restitué le {detail.deposit_restitution_date} — retenu {formatEuro(detail.deposit_retained_amount || 0)}, restitué {formatEuro(detail.deposit_restituted_amount || 0)}</div>
                )}
                {(detail.deposit_retentions || []).length > 0 && (
                  <div className="mt-2 text-xs space-y-1">
                    {(detail.deposit_retentions || []).map((r, i) => (<div key={`ret-${r.label || i}-${i}`}>• {r.label} — {formatEuro(r.amount)}</div>))}
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
        <DialogFooter>
          {detail.status === "active" && <Button variant="destructive" onClick={terminate}>Résilier</Button>}
          <Button onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={reviseOpen} onOpenChange={setReviseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Révision d&apos;indice</DialogTitle>
            <DialogDescription>Formule : nouveau_loyer = loyer_actuel × (nouvel_indice / indice_base)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nouveau trimestre</Label><Input placeholder="2025-T3" value={reviseForm.new_quarter} onChange={(e) => setReviseForm({ ...reviseForm, new_quarter: e.target.value })} /></div>
            <div><Label>Nouvelle valeur</Label><Input type="number" step="0.01" value={reviseForm.new_value} onChange={(e) => setReviseForm({ ...reviseForm, new_value: e.target.value })} data-testid="revise-value" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setReviseOpen(false)}>Annuler</Button><Button onClick={revise} data-testid="revise-submit">Appliquer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

/* ------------------ Installments ------------------ */
function InstallmentsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [payingId, setPayingId] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", paid_at: new Date().toISOString().slice(0, 10), method: "virement", reference: "", notes: "" });

  const load = () => {
    const params = status !== "all" ? { status } : {};
    api.get("/rent/installments", { params }).then(({ data }) => setItems(data.items || []));
  };
  useEffect(() => { load(); }, [status]);   // eslint-disable-line

  const openPay = (inst) => {
    setPayingId(inst.id);
    setPayForm({ amount: String(inst.remaining || inst.amount_expected_total), paid_at: new Date().toISOString().slice(0, 10), method: "virement", reference: "", notes: "" });
  };
  const submitPay = async () => {
    try {
      await api.post(`/rent/installments/${payingId}/payments`, payForm);
      toast.success("Paiement enregistré");
      setPayingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const genReceipt = async (id) => {
    try {
      await api.post(`/rent/installments/${id}/receipt`);
      toast.success("Quittance générée"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const sendReminder = async (id) => {
    try {
      const { data } = await api.post(`/rent/installments/${id}/send-reminder`);
      const st = data?.email?.status;
      if (st === "sent") toast.success("Relance envoyée au locataire");
      else if (st === "logged") toast.success("Relance journalisée (mode dev — voir logs backend)");
      else toast.warning("Relance créée mais e-mail non envoyé");
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
  };

  return (
    <div className="space-y-6">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="w-56" data-testid="installment-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous</SelectItem>
          <SelectItem value="due">À payer</SelectItem>
          <SelectItem value="partial">Partiel</SelectItem>
          <SelectItem value="paid">Payé</SelectItem>
          <SelectItem value="overdue">Impayé</SelectItem>
          <SelectItem value="canceled">Annulé</SelectItem>
        </SelectContent>
      </Select>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left">
              <th className="px-6 py-3">Période</th><th className="px-6 py-3">Échéance</th>
              <th className="px-6 py-3 text-right">Attendu</th><th className="px-6 py-3 text-right">Payé</th>
              <th className="px-6 py-3 text-right">Reste</th><th className="px-6 py-3">Statut</th><th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">Aucune échéance.</td></tr>}
            {items.slice(0, 200).map((i) => (
              <tr key={i.id} data-testid={`installment-row-${i.id}`} className="hover:bg-secondary/30">
                <td className="px-6 py-3 font-medium">{i.period_label}</td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{i.due_date}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(i.amount_expected_total)}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(i.amount_paid)}</td>
                <td className={`px-6 py-3 text-right font-mono ${i.remaining > 0.01 ? "text-red-700" : "text-emerald-700"}`}>{formatEuro(i.remaining)}</td>
                <td className="px-6 py-3"><StatusBadge tone={STATUS_TONE[i.status]}>{STATUS_LABEL[i.status]}</StatusBadge></td>
                <td className="px-6 py-3 text-right space-x-1">
                  {i.status !== "paid" && i.status !== "canceled" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openPay(i)} data-testid={`pay-btn-${i.id}`}>Encaisser</Button>
                      <Button size="sm" variant="ghost"
                              title="Envoyer une relance au locataire"
                              onClick={() => sendReminder(i.id)}
                              data-testid={`reminder-btn-${i.id}`}>
                        <BellRing className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                  {i.status === "paid" && !i.receipt_id && (
                    <Button size="sm" variant="ghost" onClick={() => genReceipt(i.id)}><Receipt className="h-3 w-3" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!payingId} onOpenChange={(o) => !o && setPayingId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Enregistrer un paiement</DialogTitle>
            <DialogDescription>Plusieurs paiements sur une même échéance sont autorisés.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Montant (€)</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payment-amount" /></div>
            <div><Label>Date</Label><Input type="date" value={payForm.paid_at} onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} /></div>
            <div><Label>Mode</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["virement", "prelevement", "cheque", "especes", "carte", "autre"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Référence</Label><Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setPayingId(null)}>Annuler</Button><Button onClick={submitPay} data-testid="payment-submit">Valider</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------ Receipts ------------------ */
function ReceiptsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/rent/receipts").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  const sendByEmail = async (id) => {
    try {
      const { data } = await api.post(`/rent/receipts/${id}/send-email`);
      const st = data?.email?.status;
      if (st === "sent") toast.success("Quittance envoyée au locataire");
      else if (st === "logged") toast.success("E-mail journalisé (mode dev — voir logs backend)");
      else toast.warning("Quittance non envoyée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
  };

  return (
    <div className="card-elevated overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
          <tr className="text-left"><th className="px-6 py-3">N°</th><th className="px-6 py-3">Période</th><th className="px-6 py-3 text-right">Montant</th><th className="px-6 py-3">Émission</th><th className="px-6 py-3">Origine</th><th></th></tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground"><Receipt className="mx-auto h-8 w-8 mb-3 opacity-60" />Aucune quittance émise.</td></tr>}
          {items.map((r) => (
            <tr key={r.id} data-testid={`receipt-row-${r.id}`} className="hover:bg-secondary/30">
              <td className="px-6 py-3 font-mono text-xs">{r.receipt_number}</td>
              <td className="px-6 py-3">{r.period_label}</td>
              <td className="px-6 py-3 text-right font-mono">{formatEuro(r.amount_paid)}</td>
              <td className="px-6 py-3 text-xs text-muted-foreground">{new Date(r.issued_at).toLocaleDateString("fr-FR")}</td>
              <td className="px-6 py-3 text-xs">{r.is_manual ? "Manuelle" : "Automatique"}</td>
              <td className="px-6 py-3 text-right space-x-1">
                <Button size="sm" variant="ghost" title="Envoyer par e-mail au locataire"
                        onClick={() => sendByEmail(r.id)}
                        data-testid={`receipt-email-${r.id}`}>
                  <Mail className="h-3 w-3" />
                </Button>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/rent/receipts/${r.id}/download`} target="_blank" rel="noopener noreferrer" data-testid={`receipt-dl-${r.id}`}>
                  <Button size="sm" variant="outline"><Download className="mr-2 h-3 w-3" />PDF</Button>
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------ Alerts ------------------ */
function AlertsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/rent/alerts").then(({ data }) => setItems(data.items || [])); }, []);
  const tone = (s) => s === "critical" ? "danger" : s === "warning" ? "warning" : "info";
  return (
    <div className="space-y-3">
      {items.length === 0 && <div className="text-sm text-muted-foreground text-center py-12">Aucune alerte.</div>}
      {items.map((a, idx) => (
        <div key={`alert-${a.kind}-${a.building_id || ""}-${a.tenant_id || ""}-${idx}`} data-testid={`alert-${a.kind}`} className="card-elevated p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className={`h-4 w-4 ${a.severity === "critical" ? "text-red-700" : a.severity === "warning" ? "text-amber-700" : "text-primary"}`} />
            <div><div className="font-medium text-sm">{a.label}</div>{a.date && <div className="text-xs text-muted-foreground">{a.date}</div>}</div>
          </div>
          <StatusBadge tone={tone(a.severity)}>{a.severity}</StatusBadge>
        </div>
      ))}
    </div>
  );
}

/* ------------------ Bits ------------------ */
function Loading({ label = "Chargement…", onRetry } = {}) {
  return <SmartLoader label={label} onRetry={onRetry} compact />;
}
function MiniStat({ label, value }) {
  return <div className="rounded-xl border border-border bg-secondary/20 p-4"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div><div className="mt-1 font-serif text-xl">{value}</div></div>;
}
function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div><div className="mt-0.5 font-medium">{value}</div></div>;
}
